"use strict";

class HttpRequest {
  post;
  get;
  option;
  err;
  constructor() {
    this.post = "POST";
    this.get = "GET";
    this.option = {};
  }

  #getBusinessId() {
    try {
      const biz = window.CLIENT_BUSINESS_DATA || window.BUSINESS || null;
      if (biz?.id && Number(biz.id) > 0) {
        return String(biz.id);
      }
      if (
        typeof BSID !== "undefined" &&
        Number.isInteger(Number(BSID)) &&
        Number(BSID) > 0
      ) {
        return String(Number(BSID));
      }
      return null;
    } catch (error) {
      console.error("Error retrieving business ID:", error);
      return null;
    }
  }

  #isLegacyGovernedModule(module) {
    return new Set([
      "SuperAdminAuth",
      "Tenants",
      "ContentHub",
      "SuperAdminLeads",
      "SAReviews",
      "SuperAdminUsers",
      "SuperAdminMessages",
      "SuperAdminNotifications",
      "SuperAdminBilling",
      "SuperAdminConfig",
      "PermissionIncidents",
      "BillingCheckout",
      "BillingWebhook",
      "TakbullPaymentTest",
      "Notifications",
    ]).has(module);
  }

  #readCanonicalOperation(formData) {
    const module = String(formData.get("module") || "").trim();
    const action = String(formData.get("action") || "").trim();
    formData.set("module", module);
    formData.set("action", action);
    return { module, action };
  }

  async #prepareRequest(formData, explicitToken = null) {
    const rawModule = String(formData.get("module") || "").trim();
    if (this.#isLegacyGovernedModule(rawModule)) {
      return {
        url: SITE_URL + "Api",
        token: explicitToken,
      };
    }

    this.#readCanonicalOperation(formData);
    let token = explicitToken;
    if (!token) {
      try {
        await $Firebase.ensureInitialized();
        const user = window.firebase?.auth?.().currentUser || null;
        token = user ? await user.getIdToken() : null;
      } catch (_) {
        token = null;
      }
    }
    return {
      url: SITE_URL + "App/index.php",
      token,
    };
  }

  #compatibleSuccess(responseData) {
    const data = responseData?.data;
    return data && typeof data === "object" && !Array.isArray(data)
      ? { ...responseData, ...data, data }
      : responseData;
  }

  /** Matches PHP ApiDebugResponse host allowlist */
  #isApiDebugHost() {
    try {
      const h = window.location.hostname;
      return h === "dev.aiplaces.co.il" || h === "aiplaces-v1.local";
    } catch (e) {
      return false;
    }
  }

  #logApiDebugError(responseData) {
    if (!this.#isApiDebugHost() || !responseData || responseData.status === "success") {
      return;
    }
    if (responseData.request_id != null || responseData.debug != null) {
      console.error("[ApiDebug]", responseData.request_id || "", responseData);
    }
  }

  FormRequest(formData, formId, method = "POST", url = null) {
    return new Promise(async (resolve, reject) => {
      const formObject = new FormData();
      for (const [key, value] of formData.entries()) {
        const newKey = key.replace(formId, "");
        formObject.append(newKey, value);
      }
      if (this.#getBusinessId()) formObject.append("bsid", this.#getBusinessId());

      try {
        const prepared = await this.#prepareRequest(formObject);
        const headers = {};
        if (prepared.token) {
          headers["Authorization"] = `Bearer ${prepared.token}`;
        }
        const response = await fetch(url || prepared.url, {
          method,
          mode: 'cors',
          credentials: 'include',
          headers,
          body: formObject,
        });

        if (response.status === 413) {
          return reject({
            status: "error",
            message: "הקובץ גדול מדי. נסו להעלות קובץ קטן יותר.",
            error: "Payload Too Large",
            delay: 5000,
          });
        }

        const rawBody = await response.text();
        let responseData;
        try {
          responseData = rawBody ? JSON.parse(rawBody) : null;
        } catch (parseError) {
          this.#logApiDebugError({ status: "error", message: "Invalid JSON response", raw: rawBody });
          return reject({
            status: "error",
            message: "התרחשה שגיאה זמנית",
            error: "Invalid server response",
            delay: 4000,
          });
        }

        if (responseData?.status === "info") {
          return reject({
            status: "info",
            message: responseData?.message || responseData?.messages || "התרחשה שגיאה זמנית",
            delay: responseData?.delay || 4000,
          });
        }

        if (!response.ok || responseData?.status !== "success") {
          const errPayload = {
            status: "error",
            message: responseData?.message || responseData?.messages || "התרחשה שגיאה זמנית",
            error: responseData?.error,
            delay: responseData?.delay || 4000,
            ...responseData,
          };
          this.#logApiDebugError(errPayload);
          return reject(errPayload);
        }

        resolve(this.#compatibleSuccess(responseData));
      } catch (error) {
        reject({
          status: "error",
          message: "התרחשה שגיאה זמנית",
          error: error.message || "Network Error",
          delay: 4000,
          ...error,
        });
      }
    });
  }

  PostData(formData, fbToken = null, method = "POST", url = null) {
    return new Promise(async (resolve, reject) => {
      try {
        const prepared = await this.#prepareRequest(formData, fbToken);
        const headers = {};
        if (prepared.token) {
          headers["Authorization"] = `Bearer ${prepared.token}`;
        }

        const response = await fetch(url || prepared.url, {
          method,
          mode: 'cors',
          credentials: 'include', 
          headers,
          body: formData,
        });

        const responseData = await response.json();

        if (!response.ok || responseData?.status !== "success") {
          const errPayload = {
            status: "error",
            message: responseData?.message || responseData?.messages || "התרחשה שגיאה זמנית",
            error: responseData?.error,
            delay: responseData?.delay || 4000,
            ...responseData,
          };
          this.#logApiDebugError(errPayload);
          return reject(errPayload);
        }

        resolve(this.#compatibleSuccess(responseData));
      } catch (error) {
        reject({
          status: "error",
          message: "התרחשה שגיאה זמנית",
          error: error.message || "התרחשה שגיאה לא צפויה",
          delay: 4000,
        });
      }
    });
  }

  PromiseData(formData) {
    return this.PostData(formData);
  }
}
const $HttpRequest = new HttpRequest();
